import { useState } from "react";
import { useTranslation } from "react-i18next";
import { BsTruck } from "react-icons/bs";
import Button from "../../components/InputComponents/Button";

/**
 * One courier delivery: the parcels going to one address, and the single fee
 * for carrying them.
 *
 * It carries the same outline colour as its parcels, so the customer can see
 * at a glance which boxes this fee covers — the fee is for the run, and the
 * parcels in a run are not otherwise distinguishable in a flat list.
 */
const DeliveryFeeCard = ({ charge, palette, onPay, onPayAll, paying }) => {
   const { t } = useTranslation();
   const [method, setMethod] = useState("balance");
   const due = Number(charge.due || 0);
   const paid = Number(charge.paid || 0);
   const packsDue = Number(charge.packs_due || 0);
   const totalDue = Number((due + packsDue).toFixed(2));

   return (
      <div
         className={`p-4 md:shadow-bs1 md:rounded-lg md:bg-white relative
            border-l-4 ${palette.border} ${palette.bg}`}
      >
         <div className="flex items-center gap-3 md:flex-col md:items-start">
            <div
               className={`w-12 h-12 shrink-0 rounded-full flex items-center justify-center
                  text-white ${palette.dot}`}
            >
               <BsTruck className="text-xl" />
            </div>

            <div className="flex-1 min-w-0">
               <p className="font-bold text-sm">{t("delivery_fee")}</p>
               <p className="text-xs text-gray-500 mt-0.5 break-words">
                  {charge.address || t("no_delivery_address_yet")}
               </p>
               <p className="text-xs text-teal_600 font-semibold mt-1">
                  {t("parcels")}: {charge.trackings.length} ·{" "}
                  {t("total_weight_short")}: {charge.weight_kg} {t("kg")}
                  {charge.zone === "highland" && ` · ${t("highland")}`}
                  {charge.zone === "region" && ` · ${t("region")}`}
               </p>
               {/* A run that already happened — its parcels sit on another tab,
                   so without this it reads like a second fee for the ones below. */}
               {charge.frozen && charge.delivered_at && (
                  <p className="text-xs text-gray-600 mt-1 leading-snug break-words">
                     ✓ {t("delivered_by_courier_on")} {charge.delivered_at}
                     <span className="block text-[11px] text-gray-400">
                        {charge.trackings.slice(0, 6).join(", ")}
                        {charge.trackings.length > 6 ? ` +${charge.trackings.length - 6}` : ""}
                     </span>
                  </p>
               )}
               {/* The fee follows the combined weight, so it moves when a new
                   parcel joins — saying so up front avoids a surprise. */}
               {!charge.frozen && (
                  <p className="text-[11px] text-gray-400 mt-1 leading-snug">
                     {t("delivery_fee_note")}
                  </p>
               )}
            </div>

            <div className="flex flex-col items-end gap-1 md:items-start md:w-full">
               <p className="font-bold text-lg">
                  {Number(charge.amount).toFixed(2)} ₾
               </p>
               {paid > 0 && (
                  <p className="text-[11px] text-gray-500">
                     {t("already_paid")}: {paid.toFixed(2)} ₾
                  </p>
               )}
               {charge.cod ? (
                  /* The amount handed to OnWay already includes this fee and
                     their order cannot be edited once it has progressed, so
                     paying here would collect it a second time. */
                  <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200
                     rounded-lg px-3 py-2 leading-snug max-w-[260px] md:max-w-full">
                     {charge.cod_reason || t("pay_at_courier")}
                  </p>
               ) : due > 0 ? (
                  <div className="flex items-center gap-2 md:w-full">
                     <select
                        value={method}
                        onChange={(e) => setMethod(e.target.value)}
                        className="text-xs border border-gray-300 rounded-lg px-2 py-2 outline-none"
                     >
                        <option value="balance">{t("balance")}</option>
                        <option value="points">{t("bonus_points")}</option>
                     </select>
                     <Button
                        className="cursor-pointer flex items-center justify-center min-w-[130px]"
                        shape="RoundedBorder14"
                        size="sm"
                        variant="OutlineTeal600"
                        onClick={() => onPay(charge.id, method)}
                     >
                        <div className="font-normal text-sm text-center text-white_A700">
                           {paying ? "…" : `${t("pay")} ${due.toFixed(2)} ₾`}
                        </div>
                     </Button>
                  </div>
               ) : (
                  <p className="text-xs font-semibold text-teal_600">{t("paid")}</p>
               )}

               {/* The fee and the parcels it covers go out together, so the
                   card offers to settle the whole delivery at once. */}
               {!charge.cod && totalDue > 0 && packsDue > 0 && (
                  <Button
                     className="cursor-pointer flex items-center justify-center min-w-[190px] md:w-full mt-1"
                     shape="RoundedBorder14"
                     size="sm"
                     variant="FillTeal600"
                     onClick={() => onPayAll(charge.id, method)}
                  >
                     <div className="font-normal text-sm text-center text-white_A700">
                        {paying ? "…" : `${t("pay_all")} ${totalDue.toFixed(2)} ₾`}
                     </div>
                  </Button>
               )}
               {!charge.cod && packsDue > 0 && (
                  <p className="text-[11px] text-gray-400 leading-snug text-right md:text-left">
                     {t("pay_all_delivery_note", {
                        count: charge.trackings.length,
                        packs: packsDue.toFixed(2),
                     })}
                  </p>
               )}
            </div>
         </div>
      </div>
   );
};

export default DeliveryFeeCard;
